import React, { useState } from 'react';
import { MessageDialog } from '../../shared/ui';

// CSS-in-JS スタイル定義
const styles = {
  // モーダルオーバーレイ
  modalOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
    padding: '20px'
  },
  // モーダルコンテンツ
  modalContent: {
    backgroundColor: 'white',
    borderRadius: '8px',
    padding: '20px',
    width: '100%',
    maxWidth: '500px',
    maxHeight: '90vh',
    overflow: 'auto'
  },
  // モーダルタイトル
  modalTitle: {
    fontSize: '18px',
    fontWeight: 'bold',
    textAlign: 'center',
    marginBottom: '20px',
    color: '#333'
  },
  // フォームテーブル
  formTable: {
    width: '100%',
    borderCollapse: 'collapse',
    marginBottom: '20px'
  },
  // フォームヘッダー
  formTh: {
    width: '120px',
    height: '40px',
    fontSize: '12px',
    background: '#ece6e6',
    border: '1px solid #000000',
    padding: '8px',
    textAlign: 'left',
    verticalAlign: 'middle',
    fontWeight: 'bold',
    whiteSpace: 'nowrap'
  },
  // フォームセル
  formTd: {
    height: '40px',
    border: '1px solid #000000',
    padding: '8px',
    position: 'relative'
  },
  // フォーム入力フィールド
  formInput: {
    width: '100%',
    height: '24px',
    border: 'none',
    outline: 'none',
    fontSize: '12px',
    fontWeight: 'bold'
  },
  // パスワードコンテナ
  passwordContainer: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center'
  },
  // パスワード入力フィールド
  passwordInput: {
    flex: 1,
    height: '24px',
    border: 'none',
    outline: 'none',
    fontSize: '12px',
    fontWeight: 'bold'
  },
  // パスワード表示切り替えボタン
  passwordToggle: {
    marginLeft: '8px',
    marginRight: '5px',
    cursor: 'pointer',
    padding: '2px',
    borderRadius: '4px',
    transition: 'background-color 0.2s ease',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '20px',
    height: '20px',
    flexShrink: 0
  },
  // パスワード表示切り替えボタンホバー時
  passwordToggleHover: {
    backgroundColor: '#f0f0f0'
  },
  // ボタンコンテナ
  buttonContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: '10px'
  },
  // ボタン
  button: {
    flex: 1,
    height: '32px',
    background: '#2B2222',
    color: '#ffffff',
    border: 'none',
    borderRadius: '16px',
    fontSize: '14px',
    cursor: 'pointer',
    transition: 'background-color 0.3s ease',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  // キャンセルボタン
  cancelButton: {
    background: '#2B2222',
  }
};

const UserInfoUpdateModal = ({ 
  isOpen, 
  onClose, 
  onUpdate, 
  userInfo,
  isProcessing = false // 処理中フラグ
}) => {
  // UI状態
  const [formData, setFormData] = useState({
    userName: userInfo?.userName || '',
    mailAddress: userInfo?.mailAddress || '',
    loginPassword: '',
    loginPassword2: ''
  }); // フォームデータ
  const [showPassword1, setShowPassword1] = useState(false); // パスワード1表示フラグ
  const [showPassword2, setShowPassword2] = useState(false); // パスワード2表示フラグ

  // フォームデータ更新処理
  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // 更新処理
  const handleSubmit = () => {
    // バリデーション
    if (!formData.userName.trim()) {
      MessageDialog.show('エラー', 'ユーザー名を入力してください。', null, 'error');
      return;
    }
    if (!formData.mailAddress.trim()) {
      MessageDialog.show('エラー', 'メールアドレスを入力してください。', null, 'error');
      return;
    }
    
    // メールアドレスの有効性チェック
    const emailRegex = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;
    if (!emailRegex.test(formData.mailAddress)) {
      MessageDialog.show('エラー', '有効なメールアドレスを入力してください。', null, 'error');
      return;
    }
    
    if (formData.loginPassword !== formData.loginPassword2) {
      MessageDialog.show('エラー', '確認用パスワードが一致しません。', null, 'error');
      return;
    }

    onUpdate(formData);
  };

  // モーダル初期化処理
  React.useEffect(() => {
    if (isOpen && userInfo) {
      setFormData({
        userName: userInfo.userName || '',
        mailAddress: userInfo.mailAddress || '',
        loginPassword: '',
        loginPassword2: ''
      });
      setShowPassword1(false);
      setShowPassword2(false);
    }
  }, [isOpen, userInfo]);

  // モーダルが閉じている場合は何も表示しない
  if (!isOpen) return null;

  return (
    <div style={styles.modalOverlay}>
      <div style={styles.modalContent}>
        <div style={styles.modalTitle}>ユーザー情報更新</div>
        
        <table style={styles.formTable}>
          <tbody>
            <tr>
              <th style={styles.formTh}>ユーザー名</th>
              <td style={styles.formTd}>
                <input
                  type="text"
                  value={formData.userName}
                  onChange={(e) => handleInputChange('userName', e.target.value)}
                  style={styles.formInput}
                  disabled={isProcessing}
                />
              </td>
            </tr>
            <tr>
              <th style={styles.formTh}>メールアドレス</th>
              <td style={styles.formTd}>
                <input
                  type="text"
                  value={formData.mailAddress}
                  onChange={(e) => handleInputChange('mailAddress', e.target.value)}
                  onBlur={(e) => {
                    // メールアドレスバリデーション
                    const email = e.target.value;
                    if (email && !/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(email)) {
                      e.target.setCustomValidity('有効なメールアドレスを入力してください');
                    } else {
                      e.target.setCustomValidity('');
                    }
                  }}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleSubmit();
                    }
                  }}
                  style={styles.formInput}
                  disabled={isProcessing}
                />
              </td>
            </tr>
            <tr>
              <th style={styles.formTh}>パスワード</th>
              <td style={styles.formTd}>
                <div style={styles.passwordContainer}>
                  <input
                    type={showPassword1 ? 'text' : 'password'}
                    value={formData.loginPassword}
                    onChange={(e) => handleInputChange('loginPassword', e.target.value)}
                    style={styles.passwordInput}
                    disabled={isProcessing}
                  />
                  <div
                    style={{
                      ...styles.passwordToggle,
                      ...(showPassword1 ? styles.passwordToggleHover : {})
                    }}
                    onClick={() => !isProcessing && setShowPassword1(!showPassword1)}
                    title={showPassword1 ? 'パスワードを隠す' : 'パスワードを表示'}
                  >
                    {showPassword1 ? (
                      // 目を開いたアイコン（パスワード表示時）
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                        <circle cx="12" cy="12" r="3"/>
                      </svg>
                    ) : (
                      // 目を閉じたアイコン（パスワード非表示時）
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/>
                        <line x1="1" y1="1" x2="23" y2="23"/>
                      </svg>
                    )}
                  </div>
                </div>
              </td>
            </tr>
            <tr>
              <th style={styles.formTh}>パスワード(確認)</th>
              <td style={styles.formTd}>
                <div style={styles.passwordContainer}>
                  <input
                    type={showPassword2 ? 'text' : 'password'}
                    value={formData.loginPassword2}
                    onChange={(e) => handleInputChange('loginPassword2', e.target.value)}
                    style={styles.passwordInput}
                    disabled={isProcessing}
                  />
                  <div
                    style={{
                      ...styles.passwordToggle,
                      ...(showPassword2 ? styles.passwordToggleHover : {})
                    }}
                    onClick={() => !isProcessing && setShowPassword2(!showPassword2)}
                    title={showPassword2 ? 'パスワードを隠す' : 'パスワードを表示'}
                  >
                    {showPassword2 ? (
                      // 目を開いたアイコン（パスワード表示時）
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                        <circle cx="12" cy="12" r="3"/>
                      </svg>
                    ) : (
                      // 目を閉じたアイコン（パスワード非表示時）
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/>
                        <line x1="1" y1="1" x2="23" y2="23"/>
                      </svg>
                    )}
                  </div>
                </div>
              </td>
            </tr>
          </tbody>
        </table>

        <div style={styles.buttonContainer}>
          <button
            style={{ ...styles.button, ...styles.cancelButton }}
            onClick={onClose}
            disabled={isProcessing}
            onMouseEnter={(e) => {
              if (!isProcessing) e.target.style.background = '#888';
            }}
            onMouseLeave={(e) => {
              if (!isProcessing) e.target.style.background = '#666';
            }}
          >
            キャンセル
          </button>
          <button
            style={styles.button}
            onClick={handleSubmit}
            disabled={isProcessing}
            onMouseEnter={(e) => {
              if (!isProcessing) e.target.style.background = '#4a4a4a';
            }}
            onMouseLeave={(e) => {
              if (!isProcessing) e.target.style.background = '#2B2222';
            }}
          >
            {isProcessing ? '更新中...' : '更新'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default UserInfoUpdateModal;
